import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useLocation, useParams } from 'react-router-dom';

// This is a public customer receipt, NOT the internal five-second success
// modal. Never claim a payment succeeded just because the URL says "success".
const CONFIRMED = new Set(['paid', 'succeeded', 'completed', 'success']);
const formatMoney = (value, currency) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0
    ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(n)
    : null;
};

const InvoicePaymentResult = () => {
  const { invoiceId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const token = String(params.get('prt') || '').trim();
  const returnWasSuccess = params.get('payment') === 'success';
  const [result, setResult] = useState({ stage: 'loading', amountReceived: null, currency: 'CAD' });
  const [refresh, setRefresh] = useState(0);

  const requestRefresh = useCallback(() => {
    setResult((prev) => ({ ...prev, stage: 'loading' }));
    setRefresh((n) => n + 1);
  }, []);

  useEffect(() => {
    let active = true;
    let pendingAttempts = 0;
    let timeoutId = null;
    const verify = async () => {
      if (!token || !invoiceId) {
        setResult({ stage: 'error', message: 'The secure payment confirmation link is missing or expired.' });
        return;
      }
      try {
        const url = process.env.REACT_APP_NODEJS_SERVER_URL +
          '/invoices/public/reconcile?prt=' + encodeURIComponent(token);
        const response = await fetch(url, { cache: 'no-store' });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'Unable to verify payment status.');
        if (!active) return;
        if (String(data.invoiceId) !== String(invoiceId)) {
          throw new Error('This payment confirmation does not match the invoice.');
        }
        if (data.stripeVerified === true && CONFIRMED.has(String(data.status || '').toLowerCase())) {
          setResult({
            stage: 'paid',
            amountReceived: data.amountReceived,
            currency: data.currency || 'CAD',
          });
          return;
        }
        if (!returnWasSuccess) {
          setResult({ stage: 'cancelled' });
          return;
        }
        // Stripe can redirect before webhook processing completes. Do not
        // offer a second charge while a successful checkout is unconfirmed.
        setResult({ stage: 'pending' });
        if (pendingAttempts++ < 9) timeoutId = setTimeout(verify, 6000);
      } catch (error) {
        if (active) setResult({
          stage: 'error',
          message: error.message || 'Your payment status could not be verified. Please retry.',
        });
      }
    };
    setResult({ stage: 'loading' });
    verify();
    return () => {
      active = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [invoiceId, token, returnWasSuccess, refresh]);

  const paid = result.stage === 'paid';
  const verifying = result.stage === 'loading' || result.stage === 'pending';
  const title = paid
    ? 'Thank you! Your payment was received.'
    : verifying
      ? 'Confirming your payment'
      : result.stage === 'cancelled'
        ? 'Payment was not completed'
        : 'Payment confirmation unavailable';
  const description = paid
    ? 'We have verified your payment. Thank you for your business!'
    : verifying
      ? 'We are checking your payment with our payment provider. Please do not submit another payment.'
      : result.stage === 'cancelled'
        ? 'The payment was cancelled or was not completed. You can return to your invoice to review your payment options.'
        : result.message || 'We could not confirm your payment. Please try verifying again or contact Accounting.';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: '#f5f7fa', py: 6 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, textAlign: 'center' }} elevation={2}>
          {verifying
            ? <CircularProgress aria-label="Verifying payment" sx={{ mb: 2 }} />
            : paid
              ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 62, mb: 1 }} />
              : result.stage === 'cancelled'
                ? <ErrorOutlineIcon color="warning" sx={{ fontSize: 62, mb: 1 }} />
                : <ErrorOutlineIcon color="error" sx={{ fontSize: 62, mb: 1 }} />}
          <Typography variant="h5" component="h1" fontWeight={750} gutterBottom>{title}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{description}</Typography>
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2.5, mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary">Invoice</Typography>
            <Typography variant="subtitle1" fontWeight={700}>#{invoiceId}</Typography>
            {paid && formatMoney(result.amountReceived, result.currency) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Verified amount received</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {formatMoney(result.amountReceived, result.currency)}
                </Typography>
              </Box>
            )}
            {paid && <Typography color="success.main" fontWeight={700} sx={{ mt: 1.5 }}>Payment verified</Typography>}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {token && !verifying && (
              <Button variant="contained" href={'/generated-invoice/' + encodeURIComponent(invoiceId) +
                '?prt=' + encodeURIComponent(token)}>
                View invoice
              </Button>
            )}
            {(result.stage === 'error' || result.stage === 'pending') && (
              <Button variant="outlined" onClick={requestRefresh}>Verify payment again</Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Need help? Contact the Accounting team using the contact details on your invoice.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default InvoicePaymentResult;
